import { Calendar, Sun, CloudRain, Star, Plane, TrendingUp } from 'lucide-react'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Separator } from '@/components/ui/separator'
import type { PriceCalcInputs, PricingAircraftSize, FacilityType } from './price-calculator-types'
import { PRICING_AIRCRAFT_LABELS, FACILITY_TYPE_LABELS } from './price-calculator-types'

interface PriceCalculatorInputsProps {
  inputs: PriceCalcInputs
  onChange: (inputs: PriceCalcInputs) => void
}

export function PriceCalculatorInputs({ inputs, onChange }: PriceCalculatorInputsProps) {
  const update = (patch: Partial<PriceCalcInputs>) =>
    onChange({ ...inputs, ...patch })

  return (
    <div className="space-y-4">
      {/* Date inputs */}
      <div className="space-y-3">
        <div className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground">
          <Calendar className="w-3.5 h-3.5" />
          Stay Dates
        </div>
        <div className="space-y-2">
          <div>
            <Label htmlFor="check-in" className="text-xs">Check-in</Label>
            <Input
              id="check-in"
              type="date"
              value={inputs.startDate}
              onChange={(e) => update({ startDate: e.target.value })}
              className="h-9 text-sm"
            />
          </div>
          <div>
            <Label htmlFor="check-out" className="text-xs">Check-out</Label>
            <Input
              id="check-out"
              type="date"
              value={inputs.endDate}
              onChange={(e) => update({ endDate: e.target.value })}
              className="h-9 text-sm"
            />
          </div>
        </div>
      </div>

      {/* Aircraft type */}
      <div>
        <div className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground mb-1.5">
          <Plane className="w-3.5 h-3.5" />
          Aircraft Type
        </div>
        <Select
          value={inputs.aircraftSize}
          onValueChange={(v) => update({ aircraftSize: v as PricingAircraftSize })}
        >
          <SelectTrigger className="h-9 text-sm">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {Object.entries(PRICING_AIRCRAFT_LABELS).map(([key, label]) => (
              <SelectItem key={key} value={key}>
                {label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Facility type */}
      <div>
        <Label className="text-xs text-muted-foreground">Facility Type</Label>
        <Select
          value={inputs.facilityType}
          onValueChange={(v) => update({ facilityType: v as FacilityType })}
        >
          <SelectTrigger className="h-9 text-sm mt-1.5">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {Object.entries(FACILITY_TYPE_LABELS).map(([key, label]) => (
              <SelectItem key={key} value={key}>
                {label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <Separator />

      {/* Legend */}
      <div className="space-y-3">
        <div className="text-xs font-medium text-muted-foreground">Pricing Tiers</div>
        <div className="space-y-1.5">
          <LegendDot color="bg-green-500" label="Low demand" />
          <LegendDot color="bg-amber-500" label="Standard demand" />
          <LegendDot color="bg-red-500" label="Peak demand" />
        </div>

        <div className="text-xs font-medium text-muted-foreground mt-3">Price Factors</div>
        <div className="space-y-1.5 text-xs text-muted-foreground">
          <div className="flex items-center gap-1.5">
            <TrendingUp className="w-3 h-3" /> Occupancy surge
          </div>
          <div className="flex items-center gap-1.5">
            <Sun className="w-3 h-3" />
            <CloudRain className="w-3 h-3 -ml-1" /> Weather demand
          </div>
          <div className="flex items-center gap-1.5">
            <Star className="w-3 h-3" /> Holiday / event
          </div>
          <div className="flex items-center gap-1.5">
            <Plane className="w-3 h-3" /> Aircraft premium
          </div>
        </div>
      </div>
    </div>
  )
}

function LegendDot({ color, label }: { color: string; label: string }) {
  return (
    <div className="flex items-center gap-2 text-xs text-muted-foreground">
      <div className={`w-2.5 h-2.5 rounded-full ${color}`} />
      {label}
    </div>
  )
}
